/**
 * Helpers for the matter-context event Date field, which is edited with a
 * native date picker but stored as a free-form string (the extract can emit
 * ranges or prose like "Oct–Nov 2024"). A picked date is stored as ISO
 * (YYYY-MM-DD); display formats ISO/parseable values to "Oct 15, 2024" and
 * leaves anything unparseable (e.g. ranges) untouched.
 */

import { intlTag } from '../i18n';

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Best-effort conversion to an ISO date (YYYY-MM-DD) for the date input;
 *  returns '' when the value can't be parsed to a single date. */
export function toIsoDate(value: string): string {
    const v = (value ?? '').trim();
    if (!v) return '';
    if (ISO_DATE_RE.test(v)) return v;
    const t = Date.parse(v);
    if (Number.isNaN(t)) return '';
    const d = new Date(t);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** Friendly display for a stored date string. ISO date-only values are parsed
 *  as local (no timezone shift); unparseable values are returned as-is. */
export function formatEventDate(value: string): string {
    const v = (value ?? '').trim();
    if (!v) return '';
    let d: Date | null = null;
    const iso = v.match(ISO_DATE_RE);
    if (iso) {
        d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    } else {
        const t = Date.parse(v);
        if (!Number.isNaN(t)) d = new Date(t);
    }
    if (!d) return v;
    return d.toLocaleDateString(intlTag(), { year: 'numeric', month: 'short', day: 'numeric' });
}
